import { STUDENT_TABLE_SELECT_DATA } from '@Constants/inputs';
import { useTypedSelector } from '@Features/store';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { useSnackbar } from '@Providers/useSnackbar';
import {
	CancelBtn,
	SaveBtn,
} from '@Pages/Roles/pages/AddRole/Components/addRoleInfo';

import { AddressesRespondData } from '@Services/educationalInfo/educationalInfo.types';
import { getAddressesData } from '@Services/educationalInfo/educationalInfo.req';
import { FlexLayout } from '@Styled/utilities';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import { SingleStudentData } from '@Services/students/students.res.types';
import { Divider, GridLayout } from '@Styled/utilities';
import styled from 'styled-components';
import { MenuItem } from '@mui/material';
import { getEducationalInfo } from '@Features/classrooms/educationalSlice';
import { ChipFlexLayout } from '@Pages/ClassRooms/Components/AddEditClassroom.styled';
import { INFO_SCHEMA } from './Schema/Info.Schema';
import PickerDate from './Components/PickerDate';
import { StudentsRequester } from '@Pages/Students/Services/Students.Requester';
import { DevTool } from '@hookform/devtools';
import { cloneDeep } from 'lodash';
import { studentsGetSingle } from '@Pages/Students/Slice/Students.Slice';
import { Autocomplete } from '@Components/Autocomplete/Autocomplete';
import { applyBackendErrors } from '@Utils/applyBackendErrors';
export type SelectedAdderess = {
	id?: number;
	name?: string;
	cities?: Array<{
		id: number;
		name: string;
		governorate_id: number;
	}>;
};
export type SelectedCity = {
	id?: number;
	name?: string;
	governorate_id?: number;
};

/**
 * Form paths -> the label the admin actually sees, so a failed save can name
 * the offending fields. `student.address` has no input bound to it, hence the
 * entry: yup reports there when the whole address object is rejected.
 */
const FIELD_LABELS: Record<string, string> = {
	first_name: 'First Name',
	last_name: 'Last Name',
	username: 'Username',
	email: 'Email',
	gender: 'Gender',
	birth_date: 'Birth Date',
	phone_number: 'Phone no.',
	'student.school': 'School',
	'student.education_type_id': 'Diploma',
	'student.education_year_id': 'Study Year',
	'student.education_language_id': 'Study Language',
	'student.parent_relation': 'Parent Relation',
	'student.parent_phone': 'Parent Phone',
	'student.address': 'Address',
	'student.address.governorate': 'Governorate',
	'student.address.city': 'City',
};

const EditStudentForm: React.FC = () => {
	const { displaySnackbar } = useSnackbar();
	const dispatch = useDispatch();
	const history = useHistory();
	const { currentEntity } = useTypedSelector((state) => state.Students);
	const { educational_info, educational_type } = useTypedSelector(
		(state) => state.educational
	);
	const auth = useTypedSelector((state) => state.auth);
	const hasEditPermission =
		auth.user.type === 'super' || auth.permissions.has('u-st');
	const [AddressesData, setAddressesData] = useImmer<AddressesRespondData>([]);
	const { address } = currentEntity?.student;

	const defaultValues: SingleStudentData = React.useMemo(() => {
		const clone = cloneDeep(currentEntity);
		// email verification no longer exists on the platform
		delete (clone as any).email_verified;
		return clone;
	}, [currentEntity]);

	const [, setSelectedAddress] = useState<SelectedAdderess>({});
	useEffect(() => {
		dispatch(getEducationalInfo());
	}, []);

	const { control, getValues, watch, setValue, formState, handleSubmit, setError } =
		useForm<SingleStudentData>({
			mode: 'all',

			resolver: yupResolver(INFO_SCHEMA),
			defaultValues,
		});

	const watchTypes = watch('student.education_type_id');
	const watchYears = watch('student.education_year_id');
	const watchLanguages = watch('student.education_language_id');
	const watchSections = watch('student.education_section_id');
	const watchGovernorate = watch('student.address.governorate');

	// Every diploma's years/languages flattened. Kept for LABEL lookups only:
	// an env with unlinked type→year/language pivots must still display the
	// student's stored value instead of blanking it (the eduinfo-wipe
	// incident — values survive load, pruning is interaction-only below).
	const allYears = React.useMemo(
		() =>
			educational_info
				.filter((_) => _.id)
				.map(({ educationYears }) => educationYears)
				.flat()
				.filterDups('id'),
		[educational_info]
	);
	const allLanguages = React.useMemo(
		() =>
			educational_info
				.filter((_) => _.id)
				.map(({ educationLanguages }) => educationLanguages)
				.flat()
				.filterDups('id'),
		[educational_info]
	);

	// Options offered in the dropdowns: only the selected diploma's — the old
	// flatten-everything lists offered a Thanaweya student the Azhar years.
	const years = React.useMemo(() => {
		const selectedType = educational_info.find(
			(_) => String(_.id) === String(watchTypes)
		);
		return ((selectedType?.educationYears as any[]) ?? []).filterDups('id');
	}, [watchTypes, educational_info]);

	const languages = React.useMemo(() => {
		const selectedType = educational_info.find(
			(_) => String(_.id) === String(watchTypes)
		);
		return ((selectedType?.educationLanguages as any[]) ?? []).filterDups(
			'id'
		);
	}, [watchTypes, educational_info]);

	// Clear a year/language that doesn't belong to the newly chosen diploma.
	// Guarded on dirtyFields so it can only fire after the user actually
	// changed the Diploma — running unguarded at load wiped DB-valid values on
	// envs whose type→year/language pivots were unlinked.
	React.useEffect(() => {
		if (!(formState.dirtyFields as any)?.student?.education_type_id) return;
		if (
			watchYears &&
			!years.some((_: any) => String(_.id) === String(watchYears))
		) {
			setValue('student.education_year_id', null as any, {
				shouldDirty: true,
			});
		}
		if (
			watchLanguages &&
			!languages.some((_: any) => String(_.id) === String(watchLanguages))
		) {
			setValue('student.education_language_id', null as any, {
				shouldDirty: true,
			});
		}
	}, [
		watchTypes,
		years,
		languages,
		watchYears,
		watchLanguages,
		formState,
		setValue,
	]);

	/**
	 * Sections belong to ONE year, so read them off the selected year rather
	 * than flattening every year's together — that offered a Thanaweya Amma
	 * student the Baccalaureate tracks and vice versa.
	 *
	 * Which years have sections is data, not a rule to hard-code: production
	 * currently attaches them to Second Baccalaureate, Second Secondary and
	 * Third Secondary.
	 */
	const sections = React.useMemo(() => {
		if (!watchYears) return [];
		// Looked up across ALL years: year→sections is its own pivot, so a
		// stored year foreign to the (mis)linked diploma still resolves.
		const selectedYear = allYears?.find(
			(_: any) => String(_?.id) === String(watchYears)
		);
		return ((selectedYear as any)?.educationSections ?? []).filterDups('id');
	}, [watchYears, allYears]);

	const cities = React.useMemo(() => {
		if (!watchGovernorate) return;
		if (!watchGovernorate) {
			setValue('student.address.city', watchGovernorate);
		}
		const flatCities = AddressesData?.filter(
			(_) => String(_.id) === String(watchGovernorate)
		)
			.map(({ cities }) => cities)
			.flat();
		if (!flatCities) return;
		return flatCities?.filterDups('id');
	}, [watchGovernorate, AddressesData]);

	const loadAddressesData = async () => {
		try {
			const {
				data: { data },
			} = await getAddressesData();
			setAddressesData(data);
		} catch (e) {
			displaySnackbar(
				'error',
				e.response.data.message ?? 'Error in fetching Addresses data '
			);
		}
	};
	useEffect(() => {
		loadAddressesData();
	}, []);

	useEffect(() => {
		if (AddressesData.length !== 0 && address?.governorate) {
			let addressVal = null;
			for (let addre of AddressesData) {
				if (addre.id === Number(address?.governorate)) {
					addressVal = addre;
				}
			}
			if (addressVal) {
				setSelectedAddress(addressVal);
			}
		}
	}, [AddressesData]);

	/**
	 * Turns nested react-hook-form errors into the list of field labels that
	 * actually failed. Without this the only signal was a console.log, so an
	 * admin clicking SAVE on a student whose profile was never completed saw
	 * precisely nothing happen.
	 */
	const collectInvalidLabels = (node: any, path = ''): string[] => {
		if (!node || typeof node !== 'object') return [];
		if (typeof node.message === 'string') {
			return [FIELD_LABELS[path] ?? path];
		}
		return Object.entries(node).flatMap(([key, value]) =>
			collectInvalidLabels(value, path ? `${path}.${key}` : key)
		);
	};

	const _onInvalid = (errors: unknown) => {
		const labels = collectInvalidLabels(errors);
		displaySnackbar(
			'error',
			labels.length
				? `Can't save — please correct: ${labels.join(', ')}`
				: 'Please correct the highlighted fields'
		);
	};

	const _onSubmit = async (form: SingleStudentData) => {
		try {
			const { email_verified, ...payload } = form as any;
			const {
				data: { data },
			} = await StudentsRequester.getInstance().update(payload);
			dispatch(studentsGetSingle({ id: currentEntity?.uuid }));
			displaySnackbar('success', `Student updated successfully`);
		} catch (e) {
			const hasFieldErrors = applyBackendErrors(e, setError);
			const msg = hasFieldErrors
				? 'Please correct the highlighted fields'
				: e.response?.data?.message || 'Unable to update student ';
			displaySnackbar('error', msg);
		}
	};

	const { isDirty, isValid } = formState;

	return (
		<form>
			 
			<FlexLayout flexDirection="column">
				<Divider label="Educational Info" />
				<SectionLayout
					gridGap={'2rem'}
					gridTemplateColumns={{ sm: 'repeat(1,1fr)' }}
					marginBottom="2rem"
				>
					<EdFormControl
						control={control}
						name="student.school"
						label="School"
						disabled={!hasEditPermission}
					/>
				</SectionLayout>
				<SectionLayout
					gridGap={'2rem'}
					gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
				>
					<EdFormControl
						control={control}
						select
						name="student.education_type_id"
						multiple
						width="17rem"
						disabled={!hasEditPermission}
						renderValues={(value) => {
							if (!educational_type) return;
							const name =
								watchTypes &&
								educational_type?.find((_) => _?.value === watchTypes)?.name;
							return <div>{name}</div>;
						}}
						SelectProps={{
							multiple: false,
							renderValue: (selected) => {
								return (
									<ChipFlexLayout>
										{watchTypes &&
											educational_type.find((_) => _.value === watchTypes)
												?.name}
									</ChipFlexLayout>
								);
							},
						}}
						label="Diploma"
					>
						{educational_type.map((_, index) => {
							return <MenuItem value={_.value}>{_.name}</MenuItem>;
						})}
					</EdFormControl>

					<EdFormControl
						control={control}
						select
						name="student.education_year_id"
						width="17rem"
						disabled={!hasEditPermission}
						renderValues={(value) => {
							return (
								<div>
									{watchYears &&
										allYears?.find((_: any) => _?.id === watchYears)?.name}
								</div>
							);
						}}
						SelectProps={{
							multiple: false,
							renderValue: (selected) => {
								return (
									<ChipFlexLayout>
										{watchYears &&
											allYears?.find((_: any) => _?.id === watchYears)?.name}
									</ChipFlexLayout>
								);
							},
						}}
						label={'Study Year'}
					>
						{years?.map((_: any, index) => {
							return (
								<MenuItem key={`ed-year-${_.id}`} value={_.id}>
									{_.name}
								</MenuItem>
							);
						})}
					</EdFormControl>
					{/* Show it whenever the chosen year actually has sections. This used
					    to test the year's name against the literal 'Third_Secondary',
					    which production spells "Third Secondary" — so the field never
					    rendered there at all, for any student, and Second Baccalaureate
					    (which does have tracks) could never have been shown either. */}
					{sections.length > 0 && (
							<EdFormControl
								control={control}
								select
								name="student.education_section_id"
								multiple
								width="17rem"
								disabled={!hasEditPermission}
								renderValues={(value) => {
									return (
										<div>
											{watchSections &&
												sections.find((_: any) => _.id === watchSections)?.name}
										</div>
									);
								}}
								SelectProps={{
									multiple: false,
									renderValue: (selected) => {
										return (
											<ChipFlexLayout>
												{watchSections &&
													sections.find((_: any) => _.id === watchSections)
														?.name}
											</ChipFlexLayout>
										);
									},
								}}
								label="Study Branch "
							>
								{sections.map((_: any) => (
									<MenuItem key={`ed-section-${_.id}`} value={_.id}>
										{_.name}
									</MenuItem>
								))}
							</EdFormControl>
						)}
					<EdFormControl
						control={control}
						select
						name="student.education_language_id"
						multiple
						width="17rem"
						disabled={!hasEditPermission}
						renderValues={(value) => {
							return (
								<div>
									{allLanguages.find((_: any) => _.id === watchLanguages)?.name}
								</div>
							);
						}}
						SelectProps={{
							multiple: false,
							renderValue: (selected) => {
								return (
									<ChipFlexLayout>
										{watchLanguages &&
											allLanguages.find((_: any) => _.id === watchLanguages)
												?.name}
									</ChipFlexLayout>
								);
							},
						}}
						label="Study Language"
					>
						{languages.map((_: any, index) => {
							return <MenuItem value={_.id}>{_.name}</MenuItem>;
						})}
					</EdFormControl>
				</SectionLayout>

				<Divider label="Personal Info" />
				<SectionLayout
					gridGap={'2rem'}
					gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
				>
					<EdFormControl
						control={control}
						name="first_name"
						label="First Name"
						disabled={!hasEditPermission}
					/>
					<EdFormControl
						control={control}
						name="last_name"
						label="Last Name"
						disabled={!hasEditPermission}
					/>
					<EdFormControl
						control={control}
						name="phone_number"
						label="Phone no."
						disabled={!hasEditPermission}
					/>
					<EdFormControl
						control={control}
						name="email"
						label="Email"
						disabled={!hasEditPermission}
					/>
					{/* Read-only on purpose. `username` is auto-generated at signup and
					    is not accepted by StudentUpdateValidator, so edits here were
					    silently dropped — the field reported success and changed
					    nothing. It is also the key the parent portal looks a student
					    up by (findStudentForParentOrFail) and carries no unique index,
					    so it should not be freely editable until both are addressed. */}
					<EdFormControl
						control={control}
						name="username"
						label="Username"
						disabled
					/>
					<EdFormControl
						control={control}
						select
						name="gender"
						label="Gender"
						disabled={!hasEditPermission}
					>
						{STUDENT_TABLE_SELECT_DATA &&
							STUDENT_TABLE_SELECT_DATA.gender.map((opt, index) => (
								<MenuItem
									key={opt.name + opt.value}
									value={opt.value}
								>{`${opt.name}`}</MenuItem>
							))}
					</EdFormControl>
					<PickerDate
						control={control}
						name={`birth_date`}
						disabled={!hasEditPermission}
					/>
					<Controller
						control={control}
						name="student.address.governorate"
						render={({ field, fieldState }) => (
							<Autocomplete
								options={AddressesData.map((_) => ({
									id: _.id,
									label: _.name,
								}))}
								size="small"
								{...field}
								fieldState={fieldState}
								showLabel
								label="Governorate"
							/>
						)}
					/>
				</SectionLayout>
				<SectionLayout
					gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
					marginTop="2rem"
					marginBottom="2rem"
				>
					<Controller
						control={control}
						name="student.address.city"
						render={({ field, fieldState }) => (
							<Autocomplete
								options={cities?.map((_) => ({
									id: _.id,
									label: _.name,
								}))}
								size="small"
								{...field}
								fieldState={fieldState}
								showLabel
								label="City"
							/>
						)}
					/>

					<EdFormControl
						control={control}
						name="password"
						label="Password"
						// disabled={!hasEditPermission}
					/>
				</SectionLayout>
				<SectionLayout>
					<EdFormControl
						control={control}
						select
						name="student.parent_relation"
						label="Parent Relation"
						disabled={!hasEditPermission}
					>
						{STUDENT_TABLE_SELECT_DATA &&
							STUDENT_TABLE_SELECT_DATA.parent_relation.map((opt, index) => (
								<MenuItem
									key={opt.name + opt.value}
									value={opt.value}
								>{`${opt.name}`}</MenuItem>
							))}
					</EdFormControl>
					<EdFormControl
						control={control}
						name="student.parent_phone"
						label="Parent Phone"
						disabled={!hasEditPermission}
					/>
					<EdFormControl
						control={control}
						switch
						name="phone_verified"
						label="Phone verified"
						disabled={!hasEditPermission}
					/>
					<EdFormControl
						control={control}
						switch
						name="student.profile_complete"
						label="Profile complete"
						disabled={!hasEditPermission}
					/>
					<EdFormControl
						control={control}
						switch
						name="student.registration_paid"
						label="Fees paid"
						disabled={!hasEditPermission}
					/>
				</SectionLayout>
			</FlexLayout>
			{hasEditPermission && (
				<FlexLayout justifyContent="flex-end" marginTop="1.5rem" gridGap="1rem">
					<CancelBtn
						variant="contained"
						// disabled={!isDirty}
						onClick={() => history.push('/dashboard/students')}
					>
						CANCEL
					</CancelBtn>
					{/* Deliberately not disabled on !isValid: a dead button is just as
					    mute as the old silent failure. Let the click through and let
					    _onInvalid say what is wrong. */}
					<SaveBtn
						variant="contained"
						onClick={handleSubmit(_onSubmit, _onInvalid)}
					>
						SAVE
					</SaveBtn>
				</FlexLayout>
			)}
		</form>
	);
};

export const SectionLayout = styled(GridLayout)`
	margin-right: 5.5rem;
	grid-gap: 2rem;
`;

export default EditStudentForm;
